<!--
Vue3 campus maps view.
@author crm4f
-->

<template>
  <div>
         
    <div class="logoContainer">
      <img id="logo" src="../assets/logo.gif">

      <div class="pull-right d-block d-md-none">
        <button type="button" 
          class="btn btn-primary" 
          data-bs-toggle="collapse" 
          data-bs-target="#mapControls" 
          aria-expanded="true" 
          aria-controls="mapControls"
          @click="controlCollapsePressed = !controlCollapsePressed">
          <font-awesome-icon aria-label="Show Menu" icon="fa-solid fa-magnifying-glass" />
        </button>
      </div>  
    </div>

    <div class="mapViewContainer">

      <div id="mapControls" :style="!controlCollapsePressed ? 'width: 25%' : null" class="mapControls collapse collapse-horizontal d-md-block" :class="controlCollapsePressed ? 'controlCollapsePressed' : null">
        <h2 class="listHeader">Get directions</h2>
        <Search @clear="destination = null; clearMarker(); infowindow.close()" @submit="(result) => setDestination(result)" name="destination" placeholder="Find a building or room" :otherLocation=origin />
        <Search @clear="origin = null; clearMarker(); infowindow.close()" @submit="(result) => setOrigin(result)" name="origin" placeholder="Get directions from here" :otherLocation=destination />

        <div class="btn-group-vertical feature-list" role="group">
          <button type="button" @click="shareLocation()" :disabled="destination == null" class="btn btn-light">Share</button>
          <button type="button" @click="useAccessibleRoutesPressed = !useAccessibleRoutesPressed; useAccessibleRoutes = !useAccessibleRoutes;" :disabled="useAccessibleRoutesDisabled" :class="{'btn btn-light': !useAccessibleRoutesPressed, 'btn btn-success': useAccessibleRoutesPressed}">Use accessible routes</button>
          <button type="button" @click="showDirectionsPressed = !showDirectionsPressed" :disabled="showDirectionsDisabled" :class="{'btn btn-light': !showDirectionsPressed, 'btn btn-success': showDirectionsPressed}">Show directions</button>
        </div>

        <div id="directions" v-if="showDirectionsPressed && directions != null">
          <h4>Directions from {{origin.locationName}}</h4>
          <ul class="list-group">
            <li class="list-group-item" :key="index" v-for="step, index in directions">
              <span class="badge rounded-pill bg-secondary">{{ step.distance.text }}</span>
              {{ step.instructions }}
              <span v-if="!useAccessibleRoutes"><br />~{{ step.duration.text }}</span>
            </li>
          </ul>
        </div>

        <hr class="hrhalfheight">

        <div class="featuresContainer">
          <h2 class="listHeader">Show features</h2>
          <div style="width:100%" class="btn-group-vertical feature-list" role="group">
            <button type="button" @click="buildingsPressed = !buildingsPressed; showBuildings = !showBuildings; reloadMapData()" :class="{ 'btn btn-light': !buildingsPressed, 'btn btn-success': buildingsPressed }">Buildings</button>
            <div class="showFeatures" v-bind:key="index" v-for="ftr, index in ftrs">
              <button type="button"
                @click="ftr.pressed = !ftr.pressed; ftr.show = !ftr.show; (!ftr.children.length && reloadMapData())"
                :data-bs-toggle="ftr.children.length ? 'collapse' : null" 
                :data-bs-target="ftr.children.length ? '#' + ftr.name.replaceAll(' ', '').toLowerCase() + 'Collapse' : null"
                :aria-expanded="ftr.children.length ? false : null"
                :aria-controls="ftr.children.length ? ftr.name.replaceAll(' ', '').toLowerCase() + 'Collapse' : null"
                :class="{ 'btn btn-light': !ftr.pressed, 'btn btn-primary': (ftr.pressed && ftr.children.length), 'btn btn-success': (ftr.pressed  && !ftr.children.length)}" >
                {{ ftr.displayLabel ? ftr.displayLabel : ftr.name }}
                <span v-if="ftr.children.length && !ftr.pressed"><font-awesome-icon icon="fa-solid fa-arrow-down" style="margin-left:5px;color:#003865;"/></span>
                <span v-if="ftr.children.length && ftr.pressed"><font-awesome-icon icon="fa-solid fa-arrow-up" style="margin-left:5px;color:#fff;"/></span>
              </button>
              <div v-if="ftr.children.length" class='collapse' :id="ftr.name.replaceAll(' ', '').toLowerCase() + 'Collapse'">
                <button type="button" @click="child.pressed = !child.pressed; child.show = !child.show; reloadMapData()" :class="{ 'btn btn-light': !child.pressed, 'btn btn-success': child.pressed}" v-bind:key="index" v-for="child, index in ftr.children">
                  {{ child.name }}
                </button>
              </div>
            </div>
            <div class="btn" @click="go('/feedback')">
              <a class="btn btn-primary active btn-sm">Give feedback</a>
            </div>

          </div>
      </div>
    </div>

      <div class="gmapContainer">
        <GMapMap
        :center='DEFAULT_CAMPUS_LATLNG'
        :zoom='17'
        class='mapCanvas tallMap'
        id="campusMap"
        ref='mapRef'
        style="width: 100%; height: 100vh"
        :options='{
          styles: getStyles(),
          fullscreenControl: false,
          mapTypeControl: false,
          mapTypeControlOptions: {
            position: google && google.maps.ControlPosition.TOP_LEFT,
            style: google && google.maps.MapTypeControlStyle.DROPDOWN_MENU
          },
          streetViewControl: true,
          zoomControl: true
        }'>
          <GMapMarker v-if="destination != null"
           ref="destinationMarker"
          :key="index"
          :position="determinePos(destination)"
          :clickable="true"
          :draggable="false"
          @click="openInfoWindow('<h5>' + (destination.locationName) + '</h5>' + (destination.directions !== null ? '<div>' + destination.directions + '</div>' : ''), $event.latLng)"
          />
          <GMapMarker v-if="origin != null"
           ref="originMarker"
          :key="index"
          :position="determinePos(origin)"
          :clickable="true"
          :draggable="false"
          @click="openInfoWindow(origin.latLng == null ? origin.entrance.name : origin.locationName, $event.latLng)"
          /> 
        </GMapMap>
      </div>

    </div>
  </div>
</template>

<script setup>
import Search from '../components/Search'
import { onMounted, ref, watch, toRaw } from 'vue'
import { useRoute } from 'vue-router'
import { useNavUtils } from '@/composables/NavigationUtils.js'
import axios from 'axios'

const { go } = useNavUtils()

// array of objects containing features to be iterated through
const ftrs = ref([
  { name: 'Accessible features', children: [
    { name: 'Accessible routes', isRoute: true, children: [], show: false, pressed: false },
    { name: 'Accessible parking', isRoute: false, children: [], show: false, pressed: false },
    { name: 'Accessible toilets', isRoute: false, children: [], show: false, pressed: false },
  ], show: false, pressed: false },
  { name: 'Cycle features', children: [
    { name: 'Cycle routes', isRoute: true, children: [], show: false, pressed: false },
    { name: 'Bike racks', isRoute: false, children: [], show: false, pressed: false },
  ], show: false, pressed: false },
  { name: 'Toilets', isRoute: false, children: [], show: false, pressed: false },
  { name: 'Food outlets', isRoute: false, children: [], show: false, pressed: false },
  { name: 'Visitor attractions', isRoute: false, children: [], show: false, pressed: false },
  { name: 'ATMs', isRoute: false, children: [], show: false, pressed: false },
  { name: 'Showers', isRoute: false, children: [], show: false, pressed: false },
  { name: 'Prayer and reflection spaces', isRoute: false, children: [], show: false, pressed: false },
  { name: 'Baby changing', isRoute: false, children: [], show: false, pressed: false },
  { name: 'Defibrillators', isRoute: false, children: [], show: false, pressed: false },
  { name: 'Period products', isRoute: false, children: [], show: false, pressed: false },
  { name: 'Retail outlets', isRoute: false, children: [], show: false, pressed: false },
  { name: 'Water fountains', isRoute: false, children: [], show: false, pressed: false },
  { name: 'Student Support Officers', isRoute: false, children: [], show: false, pressed: false, displayLabel: 'Student Support' },
  { name: 'Lift', isRoute: false, children: [], show: false, pressed: false, displayLabel: 'Lifts' },
])

const preview = ref(false)
const route = useRoute()
const mapRef = ref(null)
const map = ref(null)
const controlCollapsePressed = ref(false)
const google = ref(null)
const featureLabels = ref([])
const buildingsPressed = ref(true)
const showBuildings = ref(true)
const useAccessibleRoutesPressed = ref(false)
const showDirectionsPressed = ref(false)
const showDirectionsDisabled = ref(true)
const useAccessibleRoutesDisabled = ref(false)
const useAccessibleRoutes = ref(false)
const waypoints = ref([])
const directionsRenderer = ref(null)
const distanceAndDuration = ref(null)
const startAddress = ref(null)
const endAddress = ref(null)
const steps = ref([])

const origin = ref(null)
const destination = ref(null)
const directions = ref(null)

const geoLocationAvailable = ref(false)
const geoLatitude = ref(null)
const geoLongitude = ref(null)
const infowindow = ref(null)

const isOfferHoldersDay = ref(false)
const exRelName = ref(null)
const uofgLifeExtraCategoryName = ref(null)

const CURRENT_LOCATION = 'Current Location'
const DEFAULT_CAMPUS_LATLNG = {lat: 55.872336, lng: -4.288118}

onMounted(() => {
  //Get additional features
  axios.get('/timetable/public/externalrelations/value')
    .then(response => {
      axios.get('/timetable/public/externalrelations/name')
        .then(response2 => {
          if(response.data){
            //Get the features and their names into arrays from the response
            const additionalFtrs = response.data.split(",")
            const additionalFtrsName = response2.data.split(",")
            //Loop through each feature and add its details to the existing features array
            additionalFtrs.forEach(
              (addFtr, index) => {
                ftrs.value.unshift(
                  { name: additionalFtrsName[index], isRoute: false, children: [], show: false, pressed: false, additionalFtrType: addFtr}
                )
              }
            )
          }
        })
    })

  mapRef.value.$gmapApiPromiseLazy().then((gmapApi) => {
    console.log('gmapApi promise fulfilled')
    google.value = gmapApi
  }).then(() => {
    mapRef.value.$mapPromise.then((mapResponse) => {
    // Wait for Google Maps to load
      map.value = mapResponse
      initGeoJson(mapResponse)
      addMapListeners(mapResponse)
      directionsRenderer.value = new google.value.maps.DirectionsRenderer({
        suppressMarkers: true,
        suppressInfoWindows: true,
        draggable: false
      })
      // Handle scenario where loc id's are supplied in url.
      initLocation()
    })
    
    let url = window.location.href
    if (url.indexOf('preview=true') > -1 ) {
      preview.value = true
    }
  })

  if (process.env.VUE_APP_MODE === 'hybrid') {
    // In hybrid app, initialise geolocation when device is ready. This stops a double warning in ios.
    document.addEventListener('deviceready', () => {
      console.log('device is ready so initialise geolocation')
      initGeoLocation()
    }, false)
  } else {
    initGeoLocation()
  }
})

/**
 * Watch for changes to origin and/or destination. 
 * When fired: 
 * - Re-evaluate whether accessible routes are viable.
 * - Check both origin & destination are present
 * - If so, allow the toggle of directions
 * - If so, fetch waypoint data given both values are on accessible routes
 * - Begin to display directions
*/
watch([origin, destination], ([newOrigin, newDestination]) => {
  toggleUseAccessibleRoutes()
  if ((newOrigin !== null) && (newDestination !== null)) {
    showDirectionsDisabled.value = false
    if (origin.value.onAccessibleRoute && destination.value.onAccessibleRoute &&
      (origin.value.entrance.name !== destination.value.entrance.name)) {
      // Get waypoint data
      axios.get('map/locations/' + origin.value.locationId + '/waypoints/' + destination.value.locationId, { withCredentials: false })
        .then(response => {
          let w = 0
          waypoints.value = []
          response.data.forEach((waypoint) => {
            if (w > 0 && w < response.data.length - 1) {
              waypoints.value.push({ location: new google.value.maps.LatLng(waypoint.lat, waypoint.lng), stopover: true })
            }
            w += 1
          })
          renderDirections(map.value, origin.value.locationName + ' Exit', determinePos(origin.value).lat, determinePos(origin.value).lng, destination.value, false)
        })
    } else {
      renderDirections(map.value, origin.value.locationName + ' Exit', determinePos(origin.value).lat, determinePos(origin.value).lng, destination.value, false)
    }
  }
})

/** 
  * Watch for changes to the 'use accessible routes' button. 
  * Render directions with appropriate node values if enabled, otherwise use regular values.
*/
watch(useAccessibleRoutes, () => {
  toggleUseAccessibleRoutes()
  if (origin.value !== null && destination.value !== null) {
    if (useAccessibleRoutes.value) {
      renderDirections(map.value, origin.value.locationName, Number(origin.value.node.latLng.lat), Number(origin.value.node.latLng.lng), destination.value, true)
      console.log('rendering an accessible route')
    } else {
      renderDirections(map.value, origin.value.locationName + ' Exit', determinePos(origin.value).lat, determinePos(origin.value).lng, destination.value, false)
    }
  }
})

/**
  * Initialise destination and origin if ids supplied in URL as to and from.
*/
function initLocation () {
  let router = toRaw(route)
  if (router.query.to) {
    setDestination(router.query.to)
  }
  if (router.query.from) {
    setOrigin(router.query.from)
  }
} 

function setDestination (destinationArg) {
  const destId = ((Array.isArray(destinationArg)) ? destinationArg[0] : destinationArg)
  axios.get('map/locations/' + destId, { withCredentials: false })
  .then(response => {
    destination.value = response.data
    map.value.panTo(determinePos(destination.value))
    let name = destination.value.entrance.name
    let content  = '<h5>' + name + '</h5>'
    if (destination.value.directions != null) {
      content += destination.value.directions
    }
    if (destination.value.accessUrl != null) {
      content += "<br><a href='" + destination.value.accessUrl + "' target='_blank'>AccessAble Guide</a>"
    }
    map.value.panTo(determinePos(destination.value))
    openInfoWindow(content, determinePos(destination.value))
  })
}

function setOrigin (originArg) {
  const originId = ((Array.isArray(originArg)) ? originArg[0] : originArg)
  if (originId == CURRENT_LOCATION) {
    origin.value = {
      locationId: CURRENT_LOCATION,
      locationName: CURRENT_LOCATION,
      latLng: { lat: geoLatitude.value, lng: geoLongitude.value },
      onAccessibleRoute: false,
      roomToRoom: false,
      directions: null,
      entrance: { name: CURRENT_LOCATION, latLng: { lat: geoLatitude.value, lng: geoLongitude.value } }
    }
    map.value.panTo(determinePos(origin.value))
    openInfoWindow('<h5>Current Location</h5>', determinePos(origin.value))
  }
  else {
    axios.get('map/locations/' + originId, { withCredentials: false })
      .then(response => {
        origin.value = response.data
        let name = origin.value.entrance.name
        let content  = '<h5>' + name + '</h5>'
        openInfoWindow(content, determinePos(origin.value))
        map.value.panTo(determinePos(origin.value))
      })
  }
}

/**
  * Adds event listeners to map.
*/
function addMapListeners(mapArg) {
  // Mouseover event listener for building overlays and labels.
  mapArg.data.addListener("mouseover", function(event) {
    if(event.feature.getGeometry().getType() == "Polygon" && showBuildings.value) {
      mapArg.data.revertStyle()
      mapArg.data.overrideStyle(event.feature, {strokeWeight: 2})
      if (event.feature.getProperty("label")?.style != null) {
        if(mapArg.getZoom() < event.feature.getProperty("showLabelAtZoom")) {
          event.feature.getProperty("label").style.visibility = "visible"
        } else {
          event.feature.getProperty("label").style.fontWeight = "bold"
        }
      }
    }
  })
   
  // Mouseout event listener for building overlays and labels.
  mapArg.data.addListener("mouseout", function(event) {
    if(event.feature.getGeometry().getType() == "Polygon" && showBuildings.value) {
      mapArg.data.revertStyle()
      if(mapArg.getZoom() < event.feature.getProperty("showLabelAtZoom")) {
        event.feature.getProperty("label").style.visibility = "hidden"
      } else {
        event.feature.getProperty("label").style.fontWeight = "normal"
      }
    }
  })
   
  // Click event listener for building overlays and labels. Displays an infowindow for the building.
  mapArg.data.addListener("click", function(event) {
    var feature = event.feature
    if(feature.getProperty("featureType") == "Building" || feature.getProperty("featureType") == "FutureBuilding") {
      let content = "<h5>"  + feature.getProperty("buildingName") + "</h5>"
      if(feature.getProperty("accessUrl") != null) {
        if(feature.getProperty("accessUrl").includes("disabledgo") || feature.getProperty("accessUrl").includes("accessable.co.uk")){
          content += "<a href='" + feature.getProperty("accessUrl") + "' target='new'>AccessAble Guide</a>"
        } else {
          content += "<a href='" + feature.getProperty("accessUrl") + "' target='new'>Link to more details</a>"
        }
      }
      openInfoWindow(content, event.latLng)
    } else if(feature.getProperty("featureType") == "BuildingGroup") {
      let content = "<h5>"  + feature.getProperty("buildingGroupName") + "</h5>"
      openInfoWindow(content, event.latLng)
      
    } else if(feature.getGeometry().getType() == "Point") {
      let content = "<h5>"  + feature.getProperty("featureName") + "</h5>"
      if(feature.getProperty("featureInfo") != null) {
        content += "<div>" + feature.getProperty("featureInfo") + "</div>"
      }
      openInfoWindow(content, event.latLng)
    } else {
      let content = feature.getProperty('featureName')
        if (feature.getProperty('featureInfo') != null) {
          content += '<div>' + feature.getProperty('featureInfo') + '</div>'
        }
        openInfoWindow(content, event.latLng)
      }
  })
}

/**
 * Method to evaulate what latLng value to supply the Gmap.
*/
function determinePos (location) {
  // Because location is usually a reactive property, it's logged as Proxy(Object) and properties cant be accessed
  // We can use vue's toRaw() to get round this
  var loc = toRaw(location)
  if (loc?.entrance.latLng !== null) {
    // attempt to use the entrance coords first
    return {lat: Number(loc.entrance.latLng.lat), lng: Number(loc.entrance.latLng.lng)}
  } else if (loc?.latLng != null) {
    // if no entrance coords, use building coords
    return {lat: Number(loc.latLng.lat), lng: Number(loc.latLng.lng)}
  } else {
    alert('Sorry, we don\'t have any information for this particular location. Resetting map.')
    origin.value = null
    destination.value = null
  }
}

/**
  * Method to handle the clearing of a specified origin/destination.
  * When a location is cleared: 
  * - Disable the directions button, as no two values present.
  * - Clear any directions that may be present on the map.
  * - Re-evaluate whether accessible routes are viable.
*/
function clearMarker() {
  showDirectionsDisabled.value = true
  showDirectionsPressed.value = false
  directionsRenderer.value.setMap(null)
  directions.value = null
  toggleUseAccessibleRoutes()
}

/**
 * Routes and renders the directions between an origin and destination.
*/

function renderDirections (map, start, fromLat, fromLng, destination, useAccessibleRoutes) {

  // Create a new directions service instance.
  let directionsService = new google.value.maps.DirectionsService()
  directionsRenderer.value.setMap(map)

  // It complains when i don't do this !!
  let waypnts = []

  if (useAccessibleRoutes) {
    waypnts = waypoints.value
  }

  // Create the route.
  directionsService.route({
    origin: { lat: fromLat, lng: fromLng },
    destination: { 
      lat: useAccessibleRoutes ? Number(destination.node.latLng.lat) : determinePos(destination).lat, 
      lng: useAccessibleRoutes ? Number(destination.node.latLng.lng) : determinePos(destination).lng
    },
    travelMode: google.value.maps.DirectionsTravelMode.WALKING,
    waypoints: waypnts
  }, function (response, status) {
    if (status === 'OK') {
      directionsRenderer.value.setDirections(response)
      writeDirections(response, start, destination, useAccessibleRoutes)
      directions.value = new Array()
      for(let r = 0; r < response.routes.length; r++) {
        let route = response.routes[r]
        for(let l = 0; l < route.legs.length; l++) {
          let leg = route.legs[l]
          for(let s = 0; s < leg.steps.length; s++) {
            let step = leg.steps[s]
            if(step.distance.value > 1) {
              formatInstructions(step)
              directions.value.push(step)
            }
          }
        }            
      } 

    } else {
      alert('Directions request failed due to ' + status)
    }
  })
}

/**
 * Disables and unticks the useAccessibleRoutes checkbox when:
 * -origin is current location
 * -origin not on an accessible route
 * -destination not on an accessible route
 * -origin and destination are in the same building
 * -origin and destination are on the same route node
 * 
 * directions are room to room.
*/
function toggleUseAccessibleRoutes() {
  useAccessibleRoutesDisabled.value = false

  if(origin.value != null) {
    if(origin.value.locationId == CURRENT_LOCATION || !origin.value.onAccessibleRoute) {
      useAccessibleRoutes.value = false
      useAccessibleRoutesDisabled.value = true
      useAccessibleRoutesPressed.value = false
      return
    }
  }

  if(destination.value != null) {
    if(!destination.value.onAccessibleRoute) {
      useAccessibleRoutes.value = false
      useAccessibleRoutesDisabled.value = true
      useAccessibleRoutesPressed.value = false
      return
    }
  }    

  if(origin.value != null &&  destination.value != null) {
    if(origin.value.locationId.substring(0, 3) == destination.value.locationId.substring(0, 3)) {
      useAccessibleRoutes.value = false
      useAccessibleRoutesDisabled.value = true
      useAccessibleRoutesPressed.value = false
      return;
    }
    
    if(origin.value.node != null && destination.value.node != null) {
      if(origin.value.node.latLng.lat == destination.value.node.latLng.lat &&
        origin.value.node.latLng.lng == destination.value.node.latLng.lng) {
        useAccessibleRoutes.value = false
        useAccessibleRoutesDisabled.value = true
        useAccessibleRoutesPressed.value = false
        return
      }
    }      
  }    
}

/**
  * Strips html tags out of instruction text and makes it more apt
  * for users that don't walk.
 */
function formatInstructions(step) {
  var instructions = step.instructions
  if(instructions != null) {
    instructions = instructions.replace(/Walk/g, "Head");
    instructions = instructions.replace(/walk/g, "head");
    instructions = instructions.replace(/Destination will/g, ". Destination will")
    instructions = instructions.replace(/Take the stairs/g, ". Take the stairs")
    instructions = String(instructions).replace(/<[^>]+>/gm, "")  
  }
  step.instructions = instructions
}

function shareLocation() {
  var url = window.location.href
    if (url.indexOf("?") > -1) {
      // Remove old query params from URL
      url = url.substring(0, url.indexOf("?"))
    }
    // Add new query params to URL
    url += "?to=" + destination.value.locationId
    if (origin.value != null && origin.value.locationId != CURRENT_LOCATION) {
      url += "&from=" + origin.value.locationId
    }
    var el = document.createElement('textarea')
    el.value = url;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    alert("A link to the map has been copied to your clipboard.");
}

// Writes the start address for custom directions text renderer.
function writeDirections (response, start, destination, useAccessibleRoutes) {
  var distance = 0
  var duration = 0
  var currentSteps = []
  steps.value = []
  // Routes loop
  response.routes.forEach((route) => {
    let l = 0
    // Legs loop
    route.legs.forEach((leg) => {
      if (l === 0) {
        writeStartAddress(leg, start, useAccessibleRoutes)
      }
      // Steps loop
      leg.steps.forEach((step) => {
        if (currentSteps.length > 0) {
          var last = currentSteps[currentSteps.length - 1]
          // Group consecutive steps with the same instructions together.
          if (last.instructions === step.instructions) {
            step.distance.value += last.distance.value
            currentSteps.splice(-1)
          }
        }
        currentSteps.push(step)
        distance += step.distance.value
        duration += step.duration.value
      })
      if (l === route.legs.length - 1) {
        writeSteps(currentSteps, useAccessibleRoutes)
        writeEndAddress(leg, destination, useAccessibleRoutes)
      }
      l += 1
    })
  })
  distanceAndDuration.value = formatDistanceAndDuration(distance, duration, useAccessibleRoutes)
}

// Writes the steps for custom directions text renderer.
function writeSteps (stepsArg, useAccessibleRoutes) {
  stepsArg.forEach((step) => {
    var text = step.instructions
    if (text != null) {
      text = text.replace(/Walk/g, 'Head')
      text = text.replace(/walk/g, 'head')
      if (useAccessibleRoutes) {
        text = text.replace(/Take the stairs/g, '')
        text = text.replace(/take the stairs/g, '')
      }
    }
    steps.value.push(text + ' (' + formatDistance(step.distance.value) + ')')
  })
}
  
// Writes the steps for custom directions text renderer.
function writeStartAddress (leg, start, useAccessibleRoutes) {
  // Add start address.
  if (useAccessibleRoutes) {
    leg.start_address = start
  } else {
    leg.start_address = 'Depart from ' + start
  }
  startAddress.value = leg.start_address
}

// Writes the end address for custom directions text renderer.
function writeEndAddress (leg, destination, useAccessibleRoutes) {
  leg.end_address = destination.locationName + ' Entrance'
  if (useAccessibleRoutes && destination.accessUrl != null) {
    leg.end_address = leg.end_address + '. See Accessibility Guide link above for building access info.'
  } else {
    leg.end_address = 'Arrive at ' + leg.end_address
    if (destination.directions != null) {
      leg.end_address = leg.end_address + '. ' + destination.directions
    }
  }
  endAddress.value = leg.end_address
}

// Formats distance and duration for custom directions text renderer.
function formatDistanceAndDuration (distance, duration, useAccessibleRoutes) {
  var distanceAndDuration = formatDistance(distance)
  if (!useAccessibleRoutes) {
    distanceAndDuration += '. '
    distanceAndDuration += formatDuration(duration)
  }
  return distanceAndDuration
}
// Formats distance for custom directions text renderer.
function formatDistance (distance) {
  if (distance < 100) {
    distance += ' m'
  } else {
    distance = distance / 1000
    distance = distance.toFixed(1)
    distance += ' km'
  }
  return distance
}

// Formats duration for custom directions text renderer.
function formatDuration (duration) {
  if (duration < 60) {
    duration = 1
  } else {
    duration = duration / 60
    duration = Math.round(duration)
  }
  if (duration === 1) {
    duration = 'About 1 min'
  } else {
    duration = 'About ' + duration + ' mins'
  }
  return duration
}

/** 
 * Function to store user's current location.
*/
function initGeoLocation () {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      function (position) {
        geoLatitude.value = position.coords.latitude
        geoLongitude.value = position.coords.longitude
        geoLocationAvailable.value = true
      },
      function (error) {
        console.log('geolocation error : ' + error.code + ' ' + error.message)
      })
  } else {
    console.log('geolocation not available')
  }
}

/**
  * Styles config for the Gmap plugin. 
*/
function getStyles () {
  return [
    { featureType: 'road', elementType: 'geometry', stylers: [ { lightness: 100 }, { visibility: 'simplified' } ] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [ { color: '#575656' } ] },
    { featureType: 'road', elementType: 'labels.text.stroke', stylers: [ { color: '#FFFFFF' } ] },
    { featureType: 'poi', elementType: 'labels', stylers: [ { visibility: 'off' } ] } 
  ]
}

// Intialises geo json overlay data
function initGeoJson (mapArg) {
  loadGeoJson(mapArg)
  setMapDataStyle(mapArg)
  // Reset the style on zoom change.
  mapArg.addListener('zoom_changed', function () {
    setMapDataStyle(mapArg)
  })
}

// function to open info windows for e specified event.
function openInfoWindow(content, latLng) {
  if (infowindow.value !== null) {
    infowindow.value.close()
  }
  infowindow.value = new google.value.maps.InfoWindow({
      content: content,
      position: latLng
  })
  infowindow.value.open(map.value)
}

// Method on refresh map data, in the event of a new feature being selected, removed, etc.
function reloadMapData () {
  loadGeoJson(map.value)
}

// Loads building overlay geo json data and (optionally) accessible routes
function loadGeoJson (mapArg) {
  clearFeatures(mapArg)
  let request = getFeaturesRequest() + '&preview=' + preview.value
  // Import FeatureLabel, wait the render over buildings
  import('@/components/FeatureLabel.js')
    .then(FeatureLabel => {
      mapArg.data.loadGeoJson(process.env.VUE_APP_WS_URL + request,
      {}, function (features) {
        initFeatures(mapArg, FeatureLabel, features)
      })
    })
}

// Method to return request string for map features.
function getFeaturesRequest () {
  function traverseFtrs (ftrs) {
    for (let i = 0; i < ftrs.length; i++) {
      if (!ftrs[i].children.length) {
        if (!ftrs[i].isRoute) {
          request += '&show' + formatFeatureName(ftrs[i].name, uofgLifeExtraCategoryName.value, ftrs[i].additionalFtrType) + '=' + ftrs[i].show
        } else {
          continue
        }
      } else {
        traverseFtrs(ftrs[i].children)
      }
    }
  }

  const accessibleFtrs = ftrs.value.find(item => item.name == 'Accessible features')
  const accessibleRoutes = accessibleFtrs.children.find(item => item.name == 'Accessible routes')
  const cycleFtrs = ftrs.value.find(item => item.name == 'Cycle features')
  const cycleRoutes = cycleFtrs.children.find(item => item.name == 'Cycle routes')
  var request = '/map/features?showBuildings=' + showBuildings.value +
  '&showAccessibleRoutes=' + accessibleRoutes.show + 
  "&showCycleRoutes=" + cycleRoutes.show
  traverseFtrs(ftrs.value)
  return request
}

// Method to convert displayed feature name to one appropriate for handling.
function formatFeatureName (name, uofgLifeExtraCategoryName, additionalFtrType = null) {
  //If addiitonal feature, return the type
  if(additionalFtrType) {
    return additionalFtrType
  }
  else if (name == 'ATMs') {
    return 'Atms'
  } else if (name == 'Visitor attractions') {
    return 'Attractions'
  } else if (name == 'Prayer and reflection spaces') {
    return 'PrayerAndReflection'
  } else {
    let words = name.split(' ')
    for (let i = 0; i < words.length; i++) {
      words[i] = words[i][0].toUpperCase() + words[i].substring(1)
    }
    return words.join('')
  }
}

// Makes on-load adjustments to geo json features
function initFeatures (map, FeatureLabel, features) {
  for (var x = 0; x < features.length; x++) {
    if (features[x].getGeometry().getType() === 'Polygon') {
      var offset
      if (features[x].getProperty('buildingName') === 'Main Building') {
        offset = { x: 0, y: 15 }
      }
      // Adjust show at zoom for mobile.
      if (Number(features[x].getProperty('showLabelAtZoom')) === 17) {
        features[x].setProperty('showLabelAtZoom', 18)
      }
      if (features[x].getProperty('featureType') === 'Building') {
        featureLabels.value.push(FeatureLabel.create(map, features[x], 'buildingName', offset))
      } else {
        featureLabels.value.push(FeatureLabel.create(map, features[x], 'buildingGroupName', offset))
      }
    }
  }
}

// Removes current map features and feature labels prior to refresh
function clearFeatures (mapArg) {
  mapArg.data.forEach(function (feature) {
    mapArg.data.remove(feature)
  })
  for (var x = 0; x < featureLabels.value.length; x++) {
    featureLabels.value[x].setMap(null)
  }
  featureLabels.value = []
}

// Set the style on geo json overlay data
function setMapDataStyle (mapArg) {
  mapArg.data.setStyle(function (feature) {
    var featureType = feature.getProperty('featureType')
    if (featureType === 'Building') {
      return getBuildingStyle()
    } else if (featureType === 'BuildingGroup') {
      return getBuildingGroupStyle()
    } else if (featureType == 'Route') {
      return {
        strokeColor: '#01608d',
        strokeWeight: 4
      }
    } else if(featureType == "CycleRoute") {
        return {
          strokeColor: '#008744',
          strokeWeight: 4
        } 
      } else {
      return {
        title: feature.getProperty('featureName'),
        icon: require('../assets/' + featureType.toLowerCase() + '.png')
      }
    }
  })
}

// Returns building overlay style
function getBuildingStyle () {
  var fillOpacity = 1
  return {
    strokeColor: '#9d2f2f',
    strokeWeight: 1,
    fillOpacity: fillOpacity,
    fillColor: '#f8f8f8'
  }
}

// Returns building group overlay style
function getBuildingGroupStyle () {
  return {
    strokeColor: '#9d2f2f',
    strokeWeight: 1,
    fillOpacity: 1,
    fillColor: '#f8f8f8'
  }
}

</script>

<style scoped>

.logoContainer {
  justify-content: space-between;
  width: 100% !important;
  display: flex;
  background-color: #01608d;
  padding: 0.8em;
}

#id {
  justify-content: left;
}

.gmapContainer {
  width: 100%;
  padding: 20px;
  height: fit-content;
}
.mapViewContainer {
  display: flex;
}

.mapControls {
  padding: 25px 10px 10px 10px;
  transition: none !important;
}

.listHeader {
  color: #003865;
  font-weight: bold;
}

.hrhalfheight {
  width: 75%;
  margin: auto;
}

.feature-list {
  margin-bottom: 15px;
}

.showFeatures, .showFeatures button {
  width: 100%;
}

.featuresContainer {
  margin-top: 15px;
}

.controlCollapsePressed {
  z-index: 1000 !important;
  position: absolute !important;
  background-color: #fff !important;
}

</style>
<style>
 .hide-desc-link {
  display:none;
}
</style>